export const DEFAULT_CUSTOM_VIEW_ID = "DEFAULT_CUSTOM";
/** @deprecated use DEFAULT_CUSTOM_VIEW_ID — kept for compatibility */
export const CUSTOM_COLLECTION_ID = DEFAULT_CUSTOM_VIEW_ID;
export const CUSTOM_VIEW_TYPE = "CUSTOM";
export const COLUMN_CUSTOMIZATION_VERSION = 1;
export const DEFAULT_CUSTOM_VIEW_NAME = "Custom View";
export const FALLBACK_STANDARD_VIEW_TYPE = "AGGREGATE";
export const COLUMN_CUSTOMIZATION_PAGE_IDS = {
  PRODUCT_ANALYTICS: "product_analytics",
  PERFORMANCE_ANALYSIS: "performance_analysis",
  CAMPAIGN_LIST_V2: "campaign_list_v2",
  TARGETING_V2: "targeting_v2",
  BID_ADJUST_V2: "bid_adjust_v2",
  SEARCH_TERM_V2: "search_term_v2",
  MARKETING_STREAM: "marketing_stream",
};

export const V2_STANDARD_VIEW_OPTIONS = [
  { label: "Performance View", value: "PERFORMANCE" },
  { label: "Funnel View", value: "FUNNEL" },
];


export const COLUMN_CUSTOMIZATION_KEY_MAPPER = {
  amazon: "amz_column_customization",
  flipkart: "fk_column_customization",
  myntra: "myn_column_customization",
};

export const getColumnCustomizationKey = (platform = "amazon") => {
  const normalized = `${platform}`.toLowerCase();
  return (
    COLUMN_CUSTOMIZATION_KEY_MAPPER[normalized] ??
    COLUMN_CUSTOMIZATION_KEY_MAPPER.amazon
  );
};

export const areColumnKeyListsEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((key, index) => key === right[index]);
};

export const getMetricColumns = (columns = []) =>
  (columns || []).filter(
    (column) => !column?.isDimension && !column?.isAttribute
  );

export const getFixedColumns = (columns = []) =>
  (columns || []).filter(
    (column) => column?.isDimension || column?.isAttribute
  );

export const getAllMetricKeys = (columns = []) =>
  getMetricColumns(columns).map((column) => column.key);

export const getMetricKeysForView = (columns = [], viewType) =>
  getMetricColumns(columns)
    .filter((column) => column?.viewType?.includes(viewType))
    .map((column) => column.key);

export const sanitizeColumnKeys = (columns = [], selectedColumnKeys = []) => {
  const validKeys = new Set(getAllMetricKeys(columns));
  return (selectedColumnKeys || []).filter((key) => validKeys.has(key));
};

/**
 * Fixed columns (dimensions + attributes) keep config order.
 * Selected metrics follow selectedMetricKeys order.
 */
export const getVisibleColumns = (columns = [], selectedMetricKeys = []) => {
  const fixedColumns = getFixedColumns(columns);
  const metricByKey = new Map(
    getMetricColumns(columns).map((column) => [column.key, column])
  );
  const metrics = (selectedMetricKeys || [])
    .map((key) => metricByKey.get(key))
    .filter(Boolean);

  return [...fixedColumns, ...metrics];
};

export const inferColumnGroup = (column = {}) => {
  if (column.group) return column.group;

  const label = `${column.shortTitle || column.title || ""} ${
    column.key || ""
  }`.toLowerCase();

  if (/(impression|click|ctr|cpc|cpm|view)/.test(label)) return "Traffic";
  if (/(spend|cost|acos)/.test(label)) return "Spend";
  if (/(order|unit|cvr|conversion)/.test(label)) return "Conversion";
  if (/(revenue|sales|roas|aov|mrp|price|asp)/.test(label)) return "Revenue";
  if (/(inventory|quantity|bsr|stock)/.test(label)) return "Inventory";
  return "Other";
};

export const groupColumns = (columns = []) => {
  const grouped = new Map();

  columns.forEach((column) => {
    const groupName = inferColumnGroup(column);
    if (!grouped.has(groupName)) {
      grouped.set(groupName, []);
    }
    grouped.get(groupName).push(column);
  });

  return Array.from(grouped.entries()).map(([label, items]) => ({
    label,
    items,
  }));
};

export const buildStandardCollections = (viewOptions = [], columns = []) =>
  viewOptions.map((option) => ({
    id: option.value,
    label: option.label,
    section: "standard",
    columnKeys: getMetricKeysForView(columns, option.value),
  }));

export const createDefaultCustomView = (selectedColumnKeys = []) => ({
  id: DEFAULT_CUSTOM_VIEW_ID,
  name: DEFAULT_CUSTOM_VIEW_NAME,
  isDefault: true,
  selectedColumnKeys: [...(selectedColumnKeys || [])],
  updatedAt: new Date().toISOString(),
});

export const getDefaultCustomSeedKeys = (
  columns = [],
  pageConfig = null,
  options = {}
) => {
  const savedDefault = (pageConfig?.views || []).find(
    (view) => view?.id === DEFAULT_CUSTOM_VIEW_ID
  );
  const savedKeys = sanitizeColumnKeys(
    columns,
    savedDefault?.selectedColumnKeys
  );
  if (savedKeys.length) return savedKeys;

  if (options.seedWithAllMetrics) {
    return getAllMetricKeys(columns);
  }

  const aggregateKeys = getMetricKeysForView(
    columns,
    FALLBACK_STANDARD_VIEW_TYPE
  );
  if (aggregateKeys.length) return aggregateKeys;

  return getAllMetricKeys(columns);
};

export const normalizePageConfig = (
  pageConfig = null,
  columns = [],
  seedOptions = {}
) => {
  const views = Array.isArray(pageConfig?.views) ? [...pageConfig.views] : [];
  const hasDefault = views.some((view) => view?.id === DEFAULT_CUSTOM_VIEW_ID);
  if (!hasDefault) {
    views.unshift(
      createDefaultCustomView(
        getDefaultCustomSeedKeys(columns, pageConfig, seedOptions)
      )
    );
  }

  const normalizedViews = views.map((view) => {
    if (view?.id === DEFAULT_CUSTOM_VIEW_ID) {
      return {
        ...view,
        id: DEFAULT_CUSTOM_VIEW_ID,
        name: DEFAULT_CUSTOM_VIEW_NAME,
        isDefault: true,
        selectedColumnKeys: sanitizeColumnKeys(
          columns,
          view?.selectedColumnKeys?.length
            ? view.selectedColumnKeys
            : getDefaultCustomSeedKeys(columns, { views }, seedOptions)
        ),
      };
    }
    return {
      ...view,
      isDefault: false,
      selectedColumnKeys: sanitizeColumnKeys(columns, view?.selectedColumnKeys),
    };
  });

  const activeViewId = pageConfig?.activeViewId || null;
  const activeExists = normalizedViews.some((view) => view.id === activeViewId);

  return {
    activeViewId: activeExists ? activeViewId : null,
    views: normalizedViews,
  };
};

export const buildCustomCollections = (
  pageConfig = null,
  columns = [],
  seedOptions = {}
) => {
  const normalized = normalizePageConfig(pageConfig, columns, seedOptions);
  return normalized.views.map((view) => ({
    id: view.id,
    label: view.name || DEFAULT_CUSTOM_VIEW_NAME,
    section: "custom",
    isDefault: Boolean(view.isDefault || view.id === DEFAULT_CUSTOM_VIEW_ID),
    columnKeys: [...(view.selectedColumnKeys || [])],
  }));
};

export const resolveMatchingCollectionId = (
  collections = [],
  selectedColumnKeys = []
) => {
  const matchingStandard = collections.find(
    (collection) =>
      collection.section === "standard" &&
      areColumnKeyListsEqual(collection.columnKeys, selectedColumnKeys)
  );
  if (matchingStandard) return matchingStandard.id;

  const matchingCustom = collections.find(
    (collection) =>
      collection.section === "custom" &&
      areColumnKeyListsEqual(collection.columnKeys, selectedColumnKeys)
  );
  if (matchingCustom) return matchingCustom.id;

  return DEFAULT_CUSTOM_VIEW_ID;
};

export const isStandardViewType = (viewType, standardOptions = []) =>
  (standardOptions || []).some((option) => option.value === viewType);

export const isCustomCollectionId = (collectionId) =>
  Boolean(collectionId) &&
  !["PERFORMANCE", "FUNNEL", "ORDER", "AGGREGATE", "DRR"].includes(collectionId);

export const resolveInitialColumnState = ({
  columns = [],
  viewType,
  pageConfig = null,
  standardOptions = [],
  seedOptions = {},
}) => {
  const normalized = normalizePageConfig(pageConfig, columns, seedOptions);

  if (isStandardViewType(viewType, standardOptions)) {
    return {
      collectionId: viewType,
      selectedColumnKeys: getMetricKeysForView(columns, viewType),
      viewType,
    };
  }

  if (viewType === CUSTOM_VIEW_TYPE || isCustomCollectionId(viewType)) {
    const activeView =
      normalized.views.find((view) => view.id === normalized.activeViewId) ||
      normalized.views.find((view) => view.id === DEFAULT_CUSTOM_VIEW_ID);

    return {
      collectionId: activeView?.id || DEFAULT_CUSTOM_VIEW_ID,
      selectedColumnKeys:
        activeView?.selectedColumnKeys?.length
          ? activeView.selectedColumnKeys
          : getDefaultCustomSeedKeys(columns, normalized, seedOptions),
      viewType: CUSTOM_VIEW_TYPE,
    };
  }

  return {
    collectionId: "PERFORMANCE",
    selectedColumnKeys: getMetricKeysForView(columns, "PERFORMANCE"),
    viewType: "PERFORMANCE",
  };
};

export const createNamedCustomView = (name, selectedColumnKeys = []) => ({
  id: `view_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  name: name?.trim() || "Untitled View",
  isDefault: false,
  selectedColumnKeys: [...(selectedColumnKeys || [])],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const emptyColumnCustomizationValue = (platform) => ({
  platform,
  version: COLUMN_CUSTOMIZATION_VERSION,
  pages: {},
});
